import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import PropertySidebar from './components/PropertySidebar';
import AccommodationCard from './components/AccommodationCard';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import SignUpModal from './components/SignUpModal';
import ProfileModal from './components/ProfileModal';
import ChatsModal from './components/ChatsModal';
import SettingsModal from './components/SettingsModal';
import ListingsModal from './components/ListingsModal';
import AnalyticsModal from './components/AnalyticsModal';
import PropertyDetailModal from './components/PropertyDetailModal';
import SkeletonCard from './components/SkeletonCard';
import api from './services/api';
import { createPropertySlug } from './utils/slugify';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [user, setUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [accommodations, setAccommodations] = useState([]);
  const [filteredAccommodations, setFilteredAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatPropertyId, setChatPropertyId] = useState(null);
  const [chatReceiverId, setChatReceiverId] = useState(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [displayCount, setDisplayCount] = useState(20);
  const [propertyModalLoading, setPropertyModalLoading] = useState(false);

  // Compute modal states from URL
  const showAuthModal = location.pathname === '/signin';
  const showSignUpModal = location.pathname === '/signup';
  const showProfileModal = location.pathname === '/profile';
  const showListingsModal = location.pathname === '/listings';
  const showChatsModal = location.pathname === '/chats';
  const showSettingsModal = location.pathname === '/settings';
  const showAnalyticsModal = location.pathname === '/analytics';
  const showPropertyDetail = location.pathname.startsWith('/property/');

  // Fetch and display property from URL
  const fetchAndShowProperty = async (propertyId) => {
    try {
      setPropertyModalLoading(true);
      const property = await api.getPropertyById(propertyId);
      setSelectedProperty(property);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      navigate('/', { replace: true });
    } finally {
      setPropertyModalLoading(false);
    }
  };

  // Load property when URL changes
  useEffect(() => {
    if (params.id && showPropertyDetail) {
      fetchAndShowProperty(params.id);
    }
  }, [params.id, showPropertyDetail]);

  useEffect(() => {
    // Check for token in URL (from OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Store token in memory
      localStorage.setItem('accessToken', token);
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Check auth with new token
      checkAuth();
    } else {
      checkAuth();
    }
    
    fetchProperties();

    // Listen for similar property clicks
    const handleOpenPropertyDetail = async (event) => {
      const property = event.detail;
      const slug = createPropertySlug(property.title || property.name, property.id);
      navigate(`/property/${slug}`);
    };

    window.addEventListener('openPropertyDetail', handleOpenPropertyDetail);
    
    return () => {
      window.removeEventListener('openPropertyDetail', handleOpenPropertyDetail);
    };
  }, []);

  // Check auth when location changes (catches OAuth redirects)
  useEffect(() => {
    // Add small delay to allow OAuth session to be saved before checking
    const timer = setTimeout(() => {
      checkAuth();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [location]);

  const checkAuth = async () => {
    try {
      const userData = await api.getUser();
      if (userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };


  const fetchProperties = async (filters = {}) => {
    try {
      setLoading(true);
      const properties = await api.getProperties(filters);
      
      // Sort by newest first (no randomization)
      const sortedProperties = properties.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setAccommodations(sortedProperties);
      setFilteredAccommodations(sortedProperties);
      
      setLoading(false);
      setDisplayCount(20);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setAccommodations([]);
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await api.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSaveProfile = async (profileData) => {
    try {
      console.log('Updating profile with data:', profileData);
      const updatedUser = await api.updateProfile(profileData);
      console.log('Profile updated successfully:', updatedUser);
      setUser(updatedUser);
      navigate('/');
      return updatedUser;
    } catch (error) {
      console.error('Failed to update profile:', error);
      console.error('Error details:', error.message);
      alert(`Failed to update profile: ${error.message}`);
      throw error;
    }
  };

  const handlePropertyClick = async (property) => {
    setPropertyModalLoading(true);
    const slug = createPropertySlug(property.title || property.name, property.id);
    navigate(`/property/${slug}`);
  };

  const handleContactOwner = (property, owner) => {
    console.log('handleContactOwner called');
    console.log('Property:', property);
    console.log('Owner:', owner);
    
    // Check if user is logged in before allowing chat
    if (!user) {
      console.log('User not logged in, redirecting to sign-in');
      navigate('/signin');
      return;
    }
    
    console.log('Setting chatPropertyId:', property.id);
    console.log('Setting chatReceiverId:', owner?.id || property.user_id);
    
    setChatPropertyId(property.id);
    setChatReceiverId(owner?.id || property.user_id);
    navigate('/chats');
  };

  const applyFilters = (query = searchQuery, propertyType = selectedPropertyType) => {
    let filtered = [...accommodations];

    // Apply search filter
    if (query && query.trim()) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(property => {
        return (
          property.title?.toLowerCase().includes(searchLower) ||
          property.location?.toLowerCase().includes(searchLower) ||
          property.property_type?.toLowerCase().includes(searchLower) ||
          property.description?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply property type filter
    if (propertyType) {
      filtered = filtered.filter(property => 
        property.property_type === propertyType || property.type === propertyType
      );
    }

    setFilteredAccommodations(filtered);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setSearching(true);
    setDisplayCount(20);
    
    // Add a delay to show loading state for better UX
    await new Promise(resolve => setTimeout(resolve, 600));
    
    applyFilters(query, selectedPropertyType);
    setSearching(false);
  };

  const handlePropertyTypeSelect = (type) => {
    setSelectedPropertyType(type);
    setDisplayCount(20);
    applyFilters(searchQuery, type);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 20);
  };

  const handleListProperty = () => {
    if (user) {
      navigate('/listings');
    } else {
      navigate('/signin');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Toaster position="top-right" />
      {/* Top background section behind navbar - ends at blue line */}
      <div className="fixed top-0 left-0 right-0 h-32 sm:h-24 bg-gradient-to-br from-blue-50 via-white to-blue-50 z-40"></div>
      <Navbar 
        user={user} 
        onSignIn={() => navigate('/signin')}
        onRegister={() => navigate('/signup')}
        onSignOut={handleSignOut}
        onProfileClick={() => navigate('/profile')}
        onListingsClick={() => navigate('/listings')}
        onChatsClick={() => navigate('/chats')}
        onSettingsClick={() => navigate('/settings')}
        onAnalyticsClick={() => navigate('/analytics')}
        onSearch={handleSearch}
        onListProperty={handleListProperty}
        searching={searching}
      />
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => navigate('/')}
        onSwitchToSignUp={() => navigate('/signup')}
      />
      <SignUpModal 
        isOpen={showSignUpModal} 
        onClose={() => navigate('/')}
        onSwitchToSignIn={() => navigate('/signin')}
      />
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => navigate('/')}
        user={user}
        onSave={handleSaveProfile}
      />
      <ListingsModal
        isOpen={showListingsModal}
        onClose={() => navigate('/')}
        user={user}
        onListingChange={fetchProperties}
      />
      <ChatsModal
        isOpen={showChatsModal}
        onClose={() => {
          navigate('/');
          setChatPropertyId(null);
          setChatReceiverId(null);
        }}
        user={user}
        initialPropertyId={chatPropertyId}
        initialReceiverId={chatReceiverId}
      />
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => navigate('/')}
        user={user}
      />
      <AnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => navigate('/')}
        user={user}
      />
      <PropertyDetailModal
        isOpen={showPropertyDetail}
        onClose={() => navigate('/')}
        property={selectedProperty}
        onContactOwner={handleContactOwner}
      />
      {propertyModalLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <aside className="w-full sm:w-16 flex-shrink-0">
            <div className="relative sm:fixed sm:top-28 sm:left-6 z-40">
              <PropertySidebar 
                selectedType={selectedPropertyType}
                onTypeSelect={handlePropertyTypeSelect}
              />
            </div>
          </aside>
          
          <main className="flex-1">
            <div className="pb-4 sm:pb-6 mb-4 sm:mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-32 flex items-center justify-center hover:border-gray-300 transition-colors duration-200">
                  <p className="text-gray-400 text-sm font-medium">Advertise Here</p>
                </div>
                <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 h-32 flex items-center justify-center hover:border-gray-300 transition-colors duration-200">
                  <p className="text-gray-400 text-sm font-medium">Advertise Here</p>
                </div>
              </div>
            </div>

            {/* Header Section */}
            <div className="mb-5 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-light text-gray-700 mb-2 leading-snug">
                Find the perfect home in Ghana with confidence.
              </h2>
              {searchQuery && (
                <p className="text-sm text-gray-500">
                  Showing search results for "<span className="font-medium text-gray-700">{searchQuery}</span>"
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative z-30">
              {loading || searching ? (
                // Show 8 skeleton cards while loading or searching
                [...Array(8)].map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              ) : filteredAccommodations.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500">
                    {accommodations.length === 0 ? 'No properties available' : 'No properties match your search'}
                  </p>
                </div>
              ) : (
                filteredAccommodations.slice(0, displayCount).map((accommodation) => (
                  <AccommodationCard 
                    key={accommodation.id} 
                    accommodation={{
                      ...accommodation,
                      name: accommodation.title || accommodation.name,
                      type: accommodation.property_type || accommodation.type
                    }}
                    onClick={() => handlePropertyClick(accommodation)}
                  />
                ))
              )}
            </div>

            {/* Load More Button */}
            {!loading && !searching && filteredAccommodations.length > displayCount && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Load More Properties ({filteredAccommodations.length - displayCount} remaining)
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <aside className="hidden sm:block w-16 flex-shrink-0"></aside>
          <div className="flex-1">
            <Footer onListYourProperty={handleListProperty} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
